import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { NarrativeBlock } from "@/components/NarrativeBlock";
import { PageHeader } from "@/components/PageHeader";
import { Sparkline } from "@/components/Sparkline";

export function ReportsPage(): JSX.Element {
  const reportsQ = useQuery({ queryKey: ["reports"], queryFn: api.reports });
  const narrativeQ = useQuery({
    queryKey: ["reports", "narrative"],
    queryFn: api.reportNarrative,
  });

  if (reportsQ.isLoading) return <main className="text-chrome-300">Loading…</main>;
  if (reportsQ.error || !reportsQ.data)
    return <main className="text-crit">Failed to load.</main>;
  const data = reportsQ.data;

  if (data.reports.length === 0) {
    return (
      <main>
        <PageHeader
          eyebrow="historic posture"
          title="Reports"
          subtitle="No completed scans yet. Each scan that finishes generates a report you can browse here."
        />
        <div className="panel p-10 text-center text-sm text-chrome-400">
          Reports populate as scans complete. The current household score lives
          in the sidebar; once you've run a scan with the shim, that score
          (and a report alongside it) will appear.
        </div>
      </main>
    );
  }

  const trend = [...data.reports]
    .sort((a, b) => a.started_at.localeCompare(b.started_at))
    .map((r) => r.score.value);

  const narrative = narrativeQ.data;

  return (
    <main>
      <PageHeader
        eyebrow="historic posture"
        title="Reports"
        subtitle="One report per scan window. Trajectory visible at a glance."
        right={
          <div className="flex items-center gap-3 rounded border border-ink-400 bg-ink-700 px-4 py-2">
            <div className="label-overline">trajectory</div>
            <Sparkline data={trend} color="#22D3EE" width={140} height={36} />
            <div className="text-cyber-400 text-glow-cyber text-sm font-semibold tabular-nums">
              {trend[trend.length - 1]}
            </div>
          </div>
        }
      />

      {narrative?.totals.current && (
        <section className="mb-8">
          <NarrativeBlock
            scopeOptions={narrative.scope_options}
            windowOptions={narrative.window_options}
            lensOptions={narrative.lens_options}
            totals={narrative.totals.current}
          />
        </section>
      )}

      <ul className="space-y-3">
        {data.reports.map((r, i) => {
          const total =
            r.findings_summary.high +
            r.findings_summary.medium +
            r.findings_summary.low || 1;
          const hp = (r.findings_summary.high / total) * 100;
          const mp = (r.findings_summary.medium / total) * 100;
          const lp = (r.findings_summary.low / total) * 100;
          return (
            <li
              key={r.id}
              className="panel relative flex items-stretch gap-6 p-6"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex w-32 flex-col items-center justify-center border-r border-ink-400 pr-6">
                <div className="text-cyber-400 text-glow-cyber font-mono text-5xl font-semibold">
                  {r.score.letter}
                </div>
                <div className="mt-1 text-xs text-chrome-300 tabular-nums">
                  {r.score.value}
                  <span className="text-chrome-500">/100</span>
                </div>
                <div className="mt-3 rounded border border-ink-400 bg-ink-600 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-chrome-300">
                  {r.kind === "cold_start" ? "cold-start" : "weekly"}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-baseline justify-between gap-4">
                  <h2 className="text-base font-semibold text-chrome-100">
                    {new Date(r.started_at).toLocaleDateString()} →{" "}
                    {new Date(r.completed_at).toLocaleDateString()}
                  </h2>
                  <span className="code text-[11px] text-chrome-400">
                    {r.scan_id.slice(0, 18)}…
                  </span>
                </div>
                <div className="label-overline mt-4">findings distribution</div>
                <div className="mt-2 flex h-2 overflow-hidden rounded">
                  {hp > 0 && (
                    <div className="bg-crit" style={{ width: `${hp}%` }} />
                  )}
                  {mp > 0 && (
                    <div className="bg-warn" style={{ width: `${mp}%` }} />
                  )}
                  {lp > 0 && (
                    <div className="bg-cyber-400" style={{ width: `${lp}%` }} />
                  )}
                </div>
                <div className="mt-3 flex gap-6 text-[11px]">
                  <span className="flex items-center gap-1.5">
                    <span className="size-2 rounded-sm bg-crit" />
                    <span className="text-crit-400 font-semibold tabular-nums">
                      {r.findings_summary.high}
                    </span>
                    <span className="text-chrome-400">high</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="size-2 rounded-sm bg-warn" />
                    <span className="text-warn-400 font-semibold tabular-nums">
                      {r.findings_summary.medium}
                    </span>
                    <span className="text-chrome-400">medium</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="size-2 rounded-sm bg-cyber-400" />
                    <span className="text-cyber-400 font-semibold tabular-nums">
                      {r.findings_summary.low}
                    </span>
                    <span className="text-chrome-400">low</span>
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </main>
  );
}
